import React, { useEffect, useState } from 'react';
import { getFieldErrors } from '../useLayoff';
import { firstErrors } from '../FormControls';
import {
  useNotificationLists,
  useSaveNotificationLists,
} from '../Review/useReview';
import styles from '../layoff.module.scss';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailList = ({ id, title, hint, emails, onChange, error }) => {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');

  const add = () => {
    const email = input.trim().toLowerCase();
    if (!email) {
      return;
    }
    if (!EMAIL.test(email)) {
      setInputError('Enter a valid email address.');
      return;
    }
    if (emails.includes(email)) {
      setInputError('This address is already in the list.');
      return;
    }
    onChange([...emails, email]);
    setInput('');
    setInputError('');
  };

  return (
    <section className={styles.list_card} aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className={styles.section_title}>
        {title}
      </h3>
      <p className={styles.hint}>{hint}</p>
      {emails.length === 0 ? (
        <p className={styles.muted}>No addresses yet.</p>
      ) : (
        <ul className={styles.chips}>
          {emails.map((email) => (
            <li key={email} className={styles.chip}>
              {email}
              <button
                type='button'
                aria-label={`Remove ${email}`}
                onClick={() => onChange(emails.filter((e) => e !== email))}
              >
                <span aria-hidden='true'>×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.add_row}>
        <label htmlFor={`${id}-input`} className={styles.visually_hidden}>
          Add an address to {title}
        </label>
        <input
          id={`${id}-input`}
          type='email'
          className={`${styles.input} ${inputError ? styles.invalid : ''}`}
          placeholder='name@example.com'
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
            setInputError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <button type='button' className='button outlined' onClick={add}>
          Add
        </button>
      </div>
      {(inputError || error) && (
        <span className={styles.error_text}>{inputError || error}</span>
      )}
    </section>
  );
};

/**
 * Settings > System Management > Lay-off Notifications: who receives finalized lay-off forms
 */
const LayoffNotificationsTab = () => {
  const { data, isLoading, isError } = useNotificationLists();
  const [lists, setLists] = useState({ payroll: [], hr: [] });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setLists({ payroll: data.payroll, hr: data.hr });
      setDirty(false);
    }
  }, [data]);

  const save = useSaveNotificationLists({
    onSuccess: () => setErrors({}),
    onError: (err) => setErrors(firstErrors(getFieldErrors(err))),
  });

  const update = (key) => (emails) => {
    setLists((prev) => ({ ...prev, [key]: emails }));
    setDirty(true);
  };

  const listError = (key) =>
    Object.entries(errors).find(([field]) => field.startsWith(key))?.[1];

  if (isLoading) {
    return <p className={styles.muted}>Loading...</p>;
  }
  if (isError) {
    return (
      <p className={styles.muted}>
        The lay-off email lists could not be loaded.
      </p>
    );
  }

  return (
    <div className={styles.settings_tab}>
      <p>
        When a lay-off is finalized, the signed Employee Discharge Form is
        emailed (as a PDF) to everyone below.
      </p>
      <div className={styles.list_cards}>
        <EmailList
          id='layoff-payroll'
          title='Payroll'
          hint='Required: lay-offs can only be sent and finalized when this list has at least one address.'
          emails={lists.payroll}
          onChange={update('payroll')}
          error={listError('payroll')}
        />
        <EmailList
          id='layoff-hr'
          title='HR'
          hint='Required: lay-offs can only be sent and finalized when this list has at least one address.'
          emails={lists.hr}
          onChange={update('hr')}
          error={listError('hr')}
        />
      </div>
      <button
        type='button'
        className='button'
        onClick={() => save.mutate(lists)}
        disabled={!dirty || save.isLoading}
      >
        {save.isLoading ? 'Saving...' : 'Save lists'}
      </button>
    </div>
  );
};

export default LayoffNotificationsTab;
